import { Box, FormControlLabel, Stack, Switch, useMediaQuery, useTheme } from '@mui/material';

import { useGetContainerTypesQuery } from '@api/container';
import { CaptureMethod, ContainerRequest, PieceType, Workflow } from '@apiContract';
import { Scrollbar } from '@components/Scrollbar';
import StorageGroup from '@components/StorageGroup';
import { useSettings } from '@hooks/useSettings';
import { CaptureOptions } from '@rdTypes/capture';
import { SortingOrder, WorkflowEnumByName, WorkflowMail, WorkflowPackages } from '@rdTypes/contractHelpers';
import { sxControllerSwitchLabel } from 'src/components/SettingsBlock/components/ControllerSwitch';

import {
  captureMethodOptions,
  getDefaultValues,
  itemTypeOptions,
  settingsNames,
  workflowMailOptions,
  workflowPackagesOptions,
} from '../../helper';
import { ReceiveConfigGroup } from './ReceiveConfigGroup';
import { ReceiveToggleButtonGroup } from './ReceiveToggleButtonGroup';
import { sxCenter, sxContainer } from './styles';

type AutomatedSettings = {
  AutomatedCaptureEnabled: boolean;
};

const automatedSettings = ['AutomatedCaptureEnabled'];

const configurableWorkflows = [Workflow.Carrier, Workflow.DateReceived, Workflow.Location];

type ReceiveSettings = {
  ReceiveMailWorkflow: WorkflowMail;
  ReceivePackagesWorkflow: WorkflowPackages;
  ReceiveSortOrder: SortingOrder;
  LockersWorkflowEnable: boolean;
  DefaultItemType?: 'Mail' | 'Package';
  LabelPrintingEnabled: boolean;
  DefaultAutoPrintLabelOnAssignment: boolean;
};

type Props = {
  options: CaptureOptions;
  isLoading: boolean;
  updateReceiveConfig: (data: Partial<CaptureOptions>) => void;
  setIsLoading: (data: boolean) => void;
};

const getDefaultPieceType = (defaultItemType?: 'Mail' | 'Package') => {
  if (!defaultItemType) return PieceType.Mail;

  return defaultItemType === 'Mail' ? PieceType.Mail : PieceType.Package;
};

const getSettingWorkflowByType = (type: PieceType, receiveSettings: ReceiveSettings) =>
  WorkflowEnumByName[receiveSettings[type === PieceType.Mail ? 'ReceiveMailWorkflow' : 'ReceivePackagesWorkflow']];

const ReceiveConfig = ({ options, isLoading, updateReceiveConfig, setIsLoading }: Props) => {
  const theme = useTheme();
  const receiveSettings = useSettings(settingsNames) as ReceiveSettings;
  const { AutomatedCaptureEnabled } = useSettings(automatedSettings, 'mailroomSettingValue') as AutomatedSettings;
  const { data: containerTypes = [], isLoading: loadingContainerTypes } = useGetContainerTypesQuery();
  const matchedMobileDevice = useMediaQuery('(pointer: coarse) and (orientation: landscape)');

  const handleChangeCaptureMethod = (data: CaptureMethod) => {
    updateReceiveConfig({ captureMethod: data });
  };

  const handleChangeStorageGroup = (data: ContainerRequest) => {
    updateReceiveConfig({ storageGroupData: data });
  };

  const handleChangeWorkflow = (value: Workflow) => {
    updateReceiveConfig({
      workflow: value,
      storageGroupData: getDefaultValues(containerTypes, receiveSettings)[value],
    });
    if (!configurableWorkflows.includes(value)) {
      setIsLoading(false);
    }
  };

  const handleChangeAutoprint = (_: any, value: boolean) => {
    updateReceiveConfig({
      autoPrintLabelOnAssignment: value,
    });
  };

  const handleChangeItemType = (value: PieceType) => {
    const nextWorkflow = getSettingWorkflowByType(value, receiveSettings);
    updateReceiveConfig({
      pieceType: value,
      workflow: nextWorkflow,
      storageGroupData: getDefaultValues(containerTypes, receiveSettings)[nextWorkflow],
    });
    if (!configurableWorkflows.includes(nextWorkflow)) {
      setIsLoading(false);
    }
  };

  if (loadingContainerTypes) return null;

  if (!Object.keys(options).length) {
    const captureMethod = CaptureMethod.Single;
    const pieceType = getDefaultPieceType(receiveSettings.DefaultItemType);
    const workflow = getSettingWorkflowByType(pieceType, receiveSettings) || Workflow.DateReceived;
    const storageGroupData = getDefaultValues(containerTypes, receiveSettings)[workflow];
    updateReceiveConfig({
      captureMethod,
      pieceType,
      workflow,
      storageGroupData,
      autoPrintLabelOnAssignment:
        options.autoPrintLabelOnAssignment ??
        (receiveSettings.DefaultAutoPrintLabelOnAssignment && receiveSettings.LabelPrintingEnabled),
    });
    return null;
  }
  const sxScrollbar = {
    backgroundColor: theme.palette.background.default,
    ...(matchedMobileDevice ? { overflow: 'unset' } : {}),
  };

  return (
    <Scrollbar sx={sxScrollbar}>
      <Box sx={sxContainer}>
        <Stack spacing={3} sx={sxCenter}>
          <ReceiveConfigGroup title="Capture Method" subtitle="Select how you would like to capture items...">
            <ReceiveToggleButtonGroup
              config={captureMethodOptions(AutomatedCaptureEnabled)}
              value={options.captureMethod || CaptureMethod.Single}
              onChange={handleChangeCaptureMethod}
            />
          </ReceiveConfigGroup>

          <ReceiveConfigGroup title="Item Type" subtitle="Select the type of items you’re receiving...">
            <ReceiveToggleButtonGroup
              config={itemTypeOptions}
              value={options.pieceType || PieceType.Mail}
              onChange={handleChangeItemType}
            />
          </ReceiveConfigGroup>

          <ReceiveConfigGroup
            title="Workflow"
            subtitle="Select the workflow you’re going to use to process these items..."
          >
            <ReceiveToggleButtonGroup
              config={
                (options.pieceType || PieceType.Mail) === PieceType.Mail
                  ? workflowMailOptions
                  : workflowPackagesOptions(receiveSettings.LockersWorkflowEnable)
              }
              value={options.workflow || WorkflowEnumByName[receiveSettings.ReceiveMailWorkflow]}
              onChange={handleChangeWorkflow}
            />
          </ReceiveConfigGroup>

          {options.workflow && configurableWorkflows.includes(options.workflow) && (
            <ReceiveConfigGroup
              sx={{ zIndex: 1 }}
              title="Storage Group"
              subtitle="Define the storage for this group of items..."
            >
              <StorageGroup
                key={options.workflow}
                workflow={options.workflow || WorkflowEnumByName[receiveSettings.ReceiveMailWorkflow]}
                containerTypes={containerTypes}
                onChange={handleChangeStorageGroup}
                onLoading={setIsLoading}
                isLoading={isLoading}
                defaultValue={
                  options.storageGroupData ||
                  getDefaultValues(containerTypes, receiveSettings)[
                    WorkflowEnumByName[
                      options.pieceType && options.pieceType === PieceType.Package
                        ? receiveSettings.ReceivePackagesWorkflow
                        : receiveSettings.ReceiveMailWorkflow
                    ]
                  ]
                }
              />
            </ReceiveConfigGroup>
          )}
          {receiveSettings.LabelPrintingEnabled &&
            options.captureMethod === CaptureMethod.Single &&
            options.pieceType === PieceType.Package && (
              <ReceiveConfigGroup title="Label Printing" subtitle="Set label printing behavior...">
                <FormControlLabel
                  key="DefaultAutoPrintLabelOnAssignment"
                  label="Automatically print a label after an item is assigned"
                  labelPlacement="start"
                  control={<Switch checked={options.autoPrintLabelOnAssignment} onChange={handleChangeAutoprint} />}
                  sx={sxControllerSwitchLabel}
                />
              </ReceiveConfigGroup>
            )}
        </Stack>
      </Box>
    </Scrollbar>
  );
};

export default ReceiveConfig;
